import { Avatar } from 'antd';
import { FC } from 'react';
import { PaymentRequestForm } from './embedded/payment-request-form';
import RequestFeedbackForm from './embedded/request-feedback-form';
import SendRefundForm from './embedded/send-refund-form';
import DOMPurify from 'dompurify';
import { DATE_TIME_FORMAT, FormatDate } from '../../../constants';
import intealthLogo from '../../../assets/intealth-logo.png';
import { PaymentRejectedForm } from './embedded/payment-rejected-form';
import { PaymentFailedForm } from './embedded/payment-failed-form';
import { PaymentCompletedForm } from './embedded/payment-completed-form';

interface ChatMessageProps {
  sentBy: string;
  message: string;
  embedding: string | undefined;
  createdAt: string;
  isAdmin: boolean;
  transactions: any;
  userInitials: string;
}

export const ChatMessage: FC<ChatMessageProps> = (props) => {
  const caseWorkerStyles = {
    background: '#d4d8f7',
    padding: '8px',
    color: 'black',
    borderRadius: '8px',
    fontWeight: '12px',
    whiteSpace: 'pre-wrap',
    maxWidth: '75%',
    display: 'inline-block'
  };

  const applicantStyles = {
    background: '#edeeef',
    padding: '8px',
    color: 'black',
    borderRadius: '8px',
    fontWeight: '12px',
    whiteSpace: 'pre-wrap',
    maxWidth: '75%',
    display: 'inline-block'
  };

  const placeHolderInitials = props.userInitials;
  const toggle = (props.sentBy === 'internal' && props.isAdmin) || (props.sentBy === 'external' && !props.isAdmin);


  const getEmbeddedComponent = () => {
    const embeddedComponents: JSX.Element[] = [];
    if (props.embedding) {
      try {
        const componentData: any = JSON.parse(props.embedding);

        Object.keys(componentData).forEach((key) => {
          const data = componentData[key];
          const { value, adhocPayment, revisionRequest } = data;

          if (value === 'documentRequestType') {
            const {completed} = data;
            embeddedComponents.push(<RequestFeedbackForm 
              key="documentRequestType" 
              isAdmin={props.isAdmin} 
              changesRequested={revisionRequest.requestedChanges} 
              completed={completed}/>);
          } else if (value === 'requestPayment') {
            embeddedComponents.push(
              <PaymentRequestForm
                key="requestPayment"
                reason={adhocPayment?.reason}
                amount={parseFloat(adhocPayment?.amount ?? '')}
                completed={data?.completed ?? false}
              />
            );
          } else if (value === 'sendRefund') {
            embeddedComponents.push(
              <SendRefundForm
                id={data.id}
                key="sendRefund"
                isAdmin={props.isAdmin}
                reason={adhocPayment?.reason}
                amount={parseFloat(adhocPayment?.amount ?? '') * -1}
                transactions={props?.transactions}
                showModal={() => {}}
              />
            );
          } else if (data.value === 'paymentRejected') {
            embeddedComponents.push(
              <PaymentRejectedForm
                key={'PaymentRejectedForm' + data.id}
                amount={parseFloat(data?.adhocPayment?.amount ?? '')}
                reason={data?.adhocPayment?.reason}
                applicantRespondedAt={data?.applicantRespondedAt}
              />
            );
          } else if (data.value === 'paymentFailed') {
            embeddedComponents.push(
              <PaymentFailedForm
                key={'PaymentFailedForm' + data.id}
                amount={parseFloat(data?.adhocPayment?.amount ?? '')}
                reason={data?.adhocPayment?.reason}
                errorMessage={data?.errorMessage}
                errorOccurredAt={data?.errorOccurredAt}
              />
            )
          } else if (data.value === 'paymentCompleted') {
            embeddedComponents.push(
              <PaymentCompletedForm
                key={'PaymentCompletedForm' + data.id}
                adhocTransactionId={data.adhocTransactionId}
                amount={parseFloat(data.adhocPayment?.amount ?? '')}
                adhocTransactions={props?.transactions?.adhocTransactions}
              />
            );
          }
        });
      } catch (error) {
        console.error('Error parsing embedding data', error);
      }
    }
    return embeddedComponents;
  };

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: toggle ? 'flex-start' : 'flex-end',
        padding: '0 10px'
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: toggle ? 'row-reverse' : 'row',
          alignItems: 'flex-start',
          width: '100%'
        }}
      >
        <div
          style={{
            marginRight: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'translateY(10px)'
          }}
        >
          <Avatar
            style={{
              marginLeft: 5,
              backgroundColor: 'white',
              color: 'black',
              border: '1px solid black'
            }}
            size="large"
          >
            {props.sentBy === 'internal' ? <img src={intealthLogo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Intealth" /> : placeHolderInitials}
          </Avatar>
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: toggle ? 'flex-end' : 'flex-start',
            width: '100%'
          }}
        >
          <div
            style={{
              color: 'grey',
              fontSize: '10px',
              textAlign: toggle ? 'right' : 'left',
              marginBottom: '4px'
            }}
          >
            {FormatDate(props.createdAt, DATE_TIME_FORMAT)}
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: toggle ? 'flex-end' : 'flex-start',
              width: '100%'
            }}
          >
            <div
              style={{
                ...(toggle ? caseWorkerStyles : applicantStyles),
                marginLeft: toggle ? 'auto' : '0',
                marginRight: toggle ? '0' : 'auto',
                maxWidth: '60%',
                textAlign: 'left',
                marginBottom: '20px'
              }}
            >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(props.message) }} /> {getEmbeddedComponent().map((component) => component)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
